import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import { ProgramPhase } from "@core/features/program";
import { ProgramPhaseItemContainer } from "../ProgramPhaseItem";
import { ProgramPhaseListNavigation } from "../ProgramPhaseListNavigation";

export type Props = {
  phases: ProgramPhase[];
  selectedPhase: ProgramPhase | undefined;
  setSelectedPhase: (phase: ProgramPhase) => void;
  isLoading: boolean;
};

export function ProgramPhaseList({
  phases,
  selectedPhase,
  setSelectedPhase,
  isLoading,
}: Props) {
  const trackRef = useRef<HTMLDivElement>(null);
  const phaseRefs = useRef<Array<HTMLDivElement | null>>([]);
  const hasAnimatedOnceRef = useRef(false);
  const [phaseContainerHeight, setPhaseContainerHeight] = useState<
    number | null
  >(null);

  const selectedIndex = useMemo(() => {
    const i = phases.findIndex((p) => p.id === selectedPhase?.id);
    return i >= 0 ? i : 0;
  }, [phases, selectedPhase]);

  // Set inert + aria-hidden on non-selected phase panels so keyboard and
  // screen-reader users don't reach hidden content.
  useLayoutEffect(() => {
    phaseRefs.current.forEach((el, i) => {
      if (!el) return;
      const isSelected = i === selectedIndex;
      if (isSelected) {
        el.removeAttribute("inert");
        el.removeAttribute("aria-hidden");
      } else {
        el.setAttribute("inert", "");
        el.setAttribute("aria-hidden", "true");
      }
    });
  }, [selectedIndex, phases]);

  // Match the outer container height to the selected phase's content height so
  // we don't reserve vertical space for the tallest phase.
  useLayoutEffect(() => {
    const selectedEl = phaseRefs.current[selectedIndex];
    const phaseObject = phases[selectedIndex];
    if (!selectedEl) return;
    if (!phaseObject?.actions || phaseObject.actions.length === 0) return;
    setPhaseContainerHeight(selectedEl.getBoundingClientRect().height + 96);
  }, [selectedIndex, phases, selectedPhase]);

  // Enable the slide transition after the first paint so the initial
  // selection snaps into place without a visible slide.
  useLayoutEffect(() => {
    if (selectedPhase) {
      hasAnimatedOnceRef.current = true;
    }
  }, [selectedPhase]);

  return (
    <>
      <div
        className="overflow-hidden w-full transition-[max-height] duration-300 ease-in-out"
        style={
          phaseContainerHeight
            ? { maxHeight: `${phaseContainerHeight}px` }
            : undefined
        }
      >
        <div
          ref={trackRef}
          className={twMerge(
            "flex flex-row items-start w-full",
            hasAnimatedOnceRef.current &&
              "transition-transform duration-300 ease-in-out"
          )}
          style={{ transform: `translateX(-${selectedIndex * 100}%)` }}
        >
          {phases.map((phase, i) => (
            <div
              key={phase.id}
              ref={(el) => {
                phaseRefs.current[i] = el;
              }}
              role="tabpanel"
              id={`phase-panel-${phase.id}`}
              aria-labelledby={`phase-tab-${phase.id}`}
              className="min-w-full"
            >
              <ProgramPhaseItemContainer phase={phase} isLoading={isLoading} />
            </div>
          ))}
        </div>
      </div>
      <ProgramPhaseListNavigation
        phases={phases}
        selectedPhase={selectedPhase}
        setSelectedPhase={setSelectedPhase}
      />
    </>
  );
}

export default ProgramPhaseList;
